import './AgentReply.css'
import { useEffect, useState, type ReactNode } from 'react'
import type { AgentWidget } from 'aevocam-rest-api-spec'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'
import { AgentWidgetView } from './AgentWidgetView'
import { widgetHasVisibleUi } from './widgetVisibility'
import { cn } from '@/utilities/misc'
import { Button } from '../action/Button'

const PLACEHOLDER_RE = /\[PLACEHOLDER:([^\]]+)\]/g

type ContentSegment =
  | { kind: 'text'; text: string; key: string }
  | { kind: 'placeholder'; name: string }

function splitContentByPlaceholders(content: string): ContentSegment[] {
  const segments: ContentSegment[] = []
  let lastIndex = 0
  // Text keys follow the preceding placeholder so they stay stable as later
  // text streams in (index-based keys would shift when new placeholders appear).
  let textKey = 'lead'

  for (const match of content.matchAll(PLACEHOLDER_RE)) {
    const name = match[1]
    if (name === undefined) continue
    const index = match.index ?? 0
    if (index > lastIndex)
      segments.push({ kind: 'text', text: content.slice(lastIndex, index), key: textKey })
    segments.push({ kind: 'placeholder', name })
    textKey = `after-${name}`
    lastIndex = index + match[0].length
  }

  if (lastIndex < content.length)
    segments.push({ kind: 'text', text: content.slice(lastIndex), key: textKey })

  return segments
}

type Props = {
  content: string
  widgets?: AgentWidget[]
  reasoning?: string
  /** True while this turn's reply is still streaming */
  streaming?: boolean
  /** True while reasoning is streaming and reply text has not started yet */
  reasoningStreaming?: boolean
  /** True when widgets just arrived from the live stream for this turn */
  fromStream?: boolean
  onStreamConsumed?: () => void
  setArticleIds?: (articleIds?: string[]) => void
  onRefreshEditor?: () => void
}

export function AgentReply({
  content,
  widgets,
  reasoning,
  streaming,
  reasoningStreaming,
  fromStream,
  onStreamConsumed,
  setArticleIds,
  onRefreshEditor,
}: Props) {

  const [reasoningExpanded, setReasoningExpanded] = useState(!!reasoningStreaming)

  useEffect(() => {
    if (!fromStream) return
    onStreamConsumed?.()
  }, [fromStream]) // onStreamConsumed intentionally omitted — one-shot clear

  useEffect(() => {
    setReasoningExpanded(!!reasoningStreaming)
  }, [reasoningStreaming])

  const widgetsByPlaceholder = new Map(
    (widgets ?? []).map(widget => [widget.placeholder, widget]),
  )
  const invisibleWidgets = widgets?.filter(widget => !widgetHasVisibleUi(widget)) ?? []
  const placedPlaceholders = new Set<string>()

  const segments = splitContentByPlaceholders(content)
  const body: ReactNode[] = []

  for (const segment of segments) {
    if (segment.kind === 'text') {
      const text = segment.text.trim()
      if (!text) continue
      body.push(
        <MarkdownViewer
          key={segment.key}
          value={text}
        />,
      )
      continue
    }

    placedPlaceholders.add(segment.name)
    const widget = widgetsByPlaceholder.get(segment.name)
    if (!widget || !widgetHasVisibleUi(widget)) continue

    body.push(
      <div
        key={segment.name}
        className='Widget'
      >
        <AgentWidgetView
          widget={widget}
          fromStream={fromStream}
          setArticleIds={setArticleIds}
        />
      </div>,
    )
  }

  // Unplaced visible widgets wait until the reply finishes (no mid-stream flash
  // when a widget arrives before its placeholder text).
  const trailingWidgets = streaming
    ? []
    : (widgets ?? []).filter(widget =>
      widgetHasVisibleUi(widget) && !placedPlaceholders.has(widget.placeholder),
    )

  const hasReasoning = !!reasoning && reasoning.length > 0

  return (
    <div className='AgentReply'>
      {hasReasoning && (
        <div className={cn('Reasoning', reasoningExpanded && 'Expanded')}>
          <Button
            variant='raw'
            label={reasoningExpanded ? 'reasoning' : 'reasoning'}
            icon={reasoningExpanded ? 'chevronUp' : 'chevronDown'}
            className='Toggle'
            onClick={() => setReasoningExpanded(prev => !prev)}
          />
          {reasoningExpanded && (
            <div className='Body'>
              <MarkdownViewer value={reasoning} />
            </div>
          )}
        </div>
      )}
      {body}
      {invisibleWidgets.map(widget => (
        <AgentWidgetView
          key={widget.placeholder}
          widget={widget}
          fromStream={fromStream}
          onRefreshEditor={onRefreshEditor}
        />
      ))}
      {trailingWidgets.length > 0 && (
        <div className='Widgets'>
          {trailingWidgets.map(widget => (
            <div
              key={widget.placeholder}
              className='Widget'
            >
              <AgentWidgetView
                widget={widget}
                fromStream={fromStream}
                setArticleIds={setArticleIds}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
